import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { fetchStudents } from "../../store/studentsSlice";
import { fetchInstructors } from "../../store/instructorsSlice";
import { createReservationsForUser } from "../../api/reservationsService";
import {
   BUSY_KEYS_MODE,
   isoForDbMatchLocalHour,
   toUtcIsoFromMoldova,
} from "../../utils/moldovaTime";

const SLOT_TIMES = [
   "07:00",
   "08:30",
   "10:00",
   "11:30",
   "13:30",
   "15:00",
   "16:30",
   "18:00",
];

const DEFAULT_TARGET = 3000;
const DEFAULT_BATCH_SIZE = 20;
const DEFAULT_BATCH_DELAY_MS = 400;
const SECTORS = ["Botanica", "Ciocana", "Buiucani"];

function sleep(ms) {
   return new Promise((resolve) => setTimeout(resolve, ms));
}

function shuffleInPlace(arr) {
   for (let i = arr.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = arr[i];
      arr[i] = arr[j];
      arr[j] = tmp;
   }
   return arr;
}

function pickRandom(arr) {
   if (!arr?.length) return null;
   return arr[Math.floor(Math.random() * arr.length)];
}

function asRole(user) {
   return String(user?.role ?? user?.userRole ?? "")
      .trim()
      .toUpperCase();
}

function isStudentUser(user) {
   const role = asRole(user);
   return role === "USER" || role === "STUDENT";
}

function toPositiveInt(v) {
   const n = Number(v);
   return Number.isInteger(n) && n > 0 ? n : null;
}

function normalizeGearbox(raw) {
   const g = String(raw ?? "")
      .trim()
      .toLowerCase();
   if (g.startsWith("auto")) return "Automat";
   return "Manual";
}

function normalizeSector(raw) {
   const s = String(raw ?? "")
      .trim()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
   if (s.includes("bui")) return "Buiucani";
   if (s.includes("cio")) return "Ciocana";
   if (s.includes("bot")) return "Botanica";
   return pickRandom(SECTORS) || "Botanica";
}

function monthInputValue(date = new Date()) {
   const y = date.getFullYear();
   const m = String(date.getMonth() + 1).padStart(2, "0");
   return `${y}-${m}`;
}

function parseMonthValue(value) {
   const match = String(value || "").match(/^(\d{4})-(\d{2})$/);
   if (!match) return null;
   const year = Number(match[1]);
   const month = Number(match[2]);
   if (!year || month < 1 || month > 12) return null;
   return { year, month };
}

/** Zile lucrătoare din lună (exclude duminica) — comportament tipic elev. */
function workingDaysInMonth(year, month) {
   const days = [];
   const last = new Date(year, month, 0).getDate();
   for (let d = 1; d <= last; d += 1) {
      const date = new Date(year, month - 1, d, 12, 0, 0, 0);
      if (date.getDay() === 0) continue;
      days.push(date);
   }
   return days;
}

function buildStartTimeIso(localDateObj, timeHHMM) {
   const utcIso = toUtcIsoFromMoldova(localDateObj, timeHHMM);
   return BUSY_KEYS_MODE === "local-match"
      ? isoForDbMatchLocalHour(utcIso)
      : utcIso;
}

/**
 * Generează un plan de rezervări „împrăștiat”, ca și cum elevii ar programa
 * treptat: max 1 lecție/elev/zi, max 1 lecție/instructor/slot.
 */
export function planSparseMonthReservations({
   year,
   month,
   targetCount,
   students,
   instructors,
}) {
   const days = workingDaysInMonth(year, month);
   const slots = [];
   for (const day of days) {
      const y = day.getFullYear();
      const m = String(day.getMonth() + 1).padStart(2, "0");
      const d = String(day.getDate()).padStart(2, "0");
      const dateStr = `${y}-${m}-${d}`;
      for (const time of SLOT_TIMES) {
         slots.push({ date: day, dateStr, time });
      }
   }

   const studentPool = shuffleInPlace([...(students || [])]);
   const instructorPool = [...(instructors || [])].filter(
      (i) => toPositiveInt(i?.id) != null,
   );

   if (!studentPool.length || !instructorPool.length || !slots.length) {
      return [];
   }

   const maxByStudents = studentPool.length * days.length;
   const maxByInstructors = instructorPool.length * slots.length;
   const hardMax = Math.min(maxByStudents, maxByInstructors);
   const target = Math.min(Math.max(0, Number(targetCount) || 0), hardMax);

   const studentDayKeys = new Set();
   const instructorSlotKeys = new Set();
   const planned = [];
   const slotBag = shuffleInPlace([...slots]);

   let attempts = 0;
   const maxAttempts = Math.max(target * 40, 2000);

   while (planned.length < target && attempts < maxAttempts) {
      attempts += 1;
      const slot = slotBag[attempts % slotBag.length];
      if (attempts % slotBag.length === 0) shuffleInPlace(slotBag);

      // densitate ușor redusă: ~70% din încercări pe sloturi aleatoare
      if (Math.random() > 0.72) continue;

      const student = pickRandom(studentPool);
      const studentId = toPositiveInt(student?.id);
      if (!studentId) continue;

      const studentDayKey = `${studentId}|${slot.dateStr}`;
      if (studentDayKeys.has(studentDayKey)) continue;

      const freeInstructors = instructorPool.filter((instr) => {
         const iid = toPositiveInt(instr.id);
         if (!iid) return false;
         return !instructorSlotKeys.has(`${iid}|${slot.dateStr}|${slot.time}`);
      });
      if (!freeInstructors.length) continue;

      const instructor = pickRandom(freeInstructors);
      const instructorId = toPositiveInt(instructor.id);
      if (!instructorId) continue;

      const gearbox = normalizeGearbox(
         instructor.gearbox ?? instructor.car?.gearbox ?? student.gearbox,
      );
      const sector = normalizeSector(
         instructor.sector ?? instructor.sectorName ?? student.sector,
      );

      planned.push({
         userId: studentId,
         instructorId,
         startTime: buildStartTimeIso(slot.date, slot.time),
         sector,
         gearbox,
         dateStr: slot.dateStr,
         time: slot.time,
      });

      studentDayKeys.add(studentDayKey);
      instructorSlotKeys.add(`${instructorId}|${slot.dateStr}|${slot.time}`);
   }

   return planned;
}

export default function TestReservationSeeder() {
   const dispatch = useDispatch();
   const studentsRaw = useSelector((s) => s.students?.list || []);
   const instructorsRaw = useSelector((s) => s.instructors?.list || []);
   const studentsLoading = useSelector((s) => s.students?.loading);
   const instructorsLoading = useSelector((s) => s.instructors?.loading);

   const [monthValue, setMonthValue] = useState(() => monthInputValue());
   const [targetCount, setTargetCount] = useState(DEFAULT_TARGET);
   const [batchSize, setBatchSize] = useState(DEFAULT_BATCH_SIZE);
   const [batchDelayMs, setBatchDelayMs] = useState(DEFAULT_BATCH_DELAY_MS);

   const [running, setRunning] = useState(false);
   const [done, setDone] = useState(0);
   const [failed, setFailed] = useState(0);
   const [total, setTotal] = useState(0);
   const [status, setStatus] = useState("idle");
   const [lastError, setLastError] = useState("");
   const [plannedPreview, setPlannedPreview] = useState(0);

   const abortRef = useRef(false);

   useEffect(() => {
      if (!studentsRaw.length) dispatch(fetchStudents());
      if (!instructorsRaw.length) dispatch(fetchInstructors());
   }, [dispatch, studentsRaw.length, instructorsRaw.length]);

   const students = useMemo(() => {
      return (studentsRaw || []).filter((u) => {
         const id = toPositiveInt(u?.id);
         if (!id) return false;
         return isStudentUser(u);
      });
   }, [studentsRaw]);

   const instructors = useMemo(() => {
      return (instructorsRaw || []).filter((i) => toPositiveInt(i?.id) != null);
   }, [instructorsRaw]);

   const monthParsed = useMemo(
      () => parseMonthValue(monthValue),
      [monthValue],
   );

   const theoreticalMax = useMemo(() => {
      if (!monthParsed) return 0;
      const days = workingDaysInMonth(monthParsed.year, monthParsed.month);
      return Math.min(
         students.length * days.length,
         instructors.length * days.length * SLOT_TIMES.length,
      );
   }, [monthParsed, students.length, instructors.length]);

   useEffect(() => {
      if (!monthParsed || running) return;
      const plan = planSparseMonthReservations({
         year: monthParsed.year,
         month: monthParsed.month,
         targetCount: Math.min(Number(targetCount) || 0, theoreticalMax),
         students,
         instructors,
      });
      setPlannedPreview(plan.length);
   }, [
      monthParsed,
      targetCount,
      theoreticalMax,
      students,
      instructors,
      running,
   ]);

   const progressPct = total > 0 ? Math.round((done / total) * 100) : 0;

   const stop = useCallback(() => {
      abortRef.current = true;
      setStatus("stopping");
   }, []);

   const start = useCallback(async () => {
      if (running) return;
      if (!monthParsed) {
         setLastError("Selectează o lună validă.");
         return;
      }
      if (!students.length) {
         setLastError("Nu există elevi în sistem.");
         return;
      }
      if (!instructors.length) {
         setLastError("Nu există instructori în sistem.");
         return;
      }

      const wanted = Math.min(
         Math.max(1, Number(targetCount) || DEFAULT_TARGET),
         theoreticalMax || Number(targetCount) || DEFAULT_TARGET,
      );

      const planned = planSparseMonthReservations({
         year: monthParsed.year,
         month: monthParsed.month,
         targetCount: wanted,
         students,
         instructors,
      });

      if (!planned.length) {
         setLastError(
            "Nu am putut genera un plan (capacitate insuficientă pentru lună).",
         );
         return;
      }

      abortRef.current = false;
      setRunning(true);
      setStatus("running");
      setDone(0);
      setFailed(0);
      setTotal(planned.length);
      setLastError("");
      setPlannedPreview(planned.length);

      const chunkSize = Math.max(1, Number(batchSize) || DEFAULT_BATCH_SIZE);
      const delay = Math.max(0, Number(batchDelayMs) || 0);
      let okCount = 0;
      let failCount = 0;

      for (let i = 0; i < planned.length; i += chunkSize) {
         if (abortRef.current) break;

         const chunk = planned.slice(i, i + chunkSize);
         const results = await Promise.allSettled(
            chunk.map((item) =>
               createReservationsForUser({
                  userId: item.userId,
                  instructorId: item.instructorId,
                  reservations: [
                     {
                        startTime: item.startTime,
                        sector: item.sector,
                        gearbox: item.gearbox,
                        privateMessage: "seed-test",
                        color: "--black-t",
                        isFavorite: false,
                        isImportant: false,
                     },
                  ],
               }),
            ),
         );

         for (const r of results) {
            if (r.status === "fulfilled") okCount += 1;
            else {
               failCount += 1;
               const msg = r.reason?.message || String(r.reason || "");
               if (msg) setLastError(msg);
            }
         }

         setDone(okCount);
         setFailed(failCount);

         if (abortRef.current) break;
         if (i + chunkSize < planned.length && delay > 0) {
            await sleep(delay);
         }
      }

      setRunning(false);
      setStatus(abortRef.current ? "stopped" : "done");
   }, [
      batchDelayMs,
      batchSize,
      instructors,
      monthParsed,
      running,
      students,
      targetCount,
      theoreticalMax,
   ]);

   const loadingLists = Boolean(studentsLoading || instructorsLoading);

   return (
      <div className="adminOther__seeder">
         <div className="adminOther__seederHead">
            <div>
               <p className="adminOther__linkMeta">Testare</p>
               <h3 className="adminOther__seederTitle">
                  Seed rezervări pe lună
               </h3>
               <p className="adminOther__seederHint">
                  Generează rezervări pe elevii și instructorii existenți, în
                  batch-uri (span), cu densitate moderată — imitând programări
                  reale, nu umplerea instant a grilei.
               </p>
            </div>
         </div>

         <div className="adminOther__seederGrid">
            <label className="adminOther__seederField">
               <span>Luna</span>
               <input
                  type="month"
                  value={monthValue}
                  onChange={(e) => setMonthValue(e.target.value)}
                  disabled={running}
               />
            </label>

            <label className="adminOther__seederField">
               <span>Țintă rezervări</span>
               <input
                  type="number"
                  min={1}
                  max={Math.max(theoreticalMax, 1)}
                  step={100}
                  value={targetCount}
                  onChange={(e) => setTargetCount(Number(e.target.value) || 0)}
                  disabled={running}
               />
            </label>

            <label className="adminOther__seederField">
               <span>Batch size (span)</span>
               <input
                  type="number"
                  min={1}
                  max={100}
                  value={batchSize}
                  onChange={(e) => setBatchSize(Number(e.target.value) || 1)}
                  disabled={running}
               />
            </label>

            <label className="adminOther__seederField">
               <span>Pauză între batch-uri (ms)</span>
               <input
                  type="number"
                  min={0}
                  max={5000}
                  step={50}
                  value={batchDelayMs}
                  onChange={(e) =>
                     setBatchDelayMs(Number(e.target.value) || 0)
                  }
                  disabled={running}
               />
            </label>
         </div>

         <div className="adminOther__seederStats">
            <span>Elevi: {students.length}</span>
            <span>Instructori: {instructors.length}</span>
            <span>Capacitate lună: {theoreticalMax}</span>
            <span>Plan estimat: {plannedPreview}</span>
         </div>

         <div className="adminOther__seederProgress">
            <div
               className="adminOther__seederProgressBar"
               style={{ width: `${progressPct}%` }}
            />
         </div>

         <div className="adminOther__seederFooter">
            <div className="adminOther__seederStatus">
               {loadingLists
                  ? "Se încarcă elevii / instructorii…"
                  : status === "idle"
                    ? "Pregătit."
                    : status === "running"
                      ? `Se creează… ${done}/${total} (eșuate: ${failed})`
                      : status === "stopping"
                        ? `Se oprește… ${done}/${total}`
                        : status === "stopped"
                          ? `Oprit. Create: ${done}, eșuate: ${failed}`
                          : `Gata. Create: ${done}, eșuate: ${failed}`}
               {lastError ? (
                  <div className="adminOther__seederError">{lastError}</div>
               ) : null}
            </div>

            <div className="adminOther__seederActions">
               {!running ? (
                  <button
                     type="button"
                     className="adminOther__seederBtn adminOther__seederBtn--primary"
                     onClick={start}
                     disabled={loadingLists || !students.length}
                  >
                     Generează pe span
                  </button>
               ) : (
                  <button
                     type="button"
                     className="adminOther__seederBtn adminOther__seederBtn--danger"
                     onClick={stop}
                  >
                     Oprește
                  </button>
               )}
            </div>
         </div>
      </div>
   );
}
